/**
 * What the board wants a human to look at, worst first — the panel that leads
 * `/ai/overview` under the KPI strip.
 *
 * This is where the board is *opinionated*, and deliberately so. The rest of the
 * page reports; this ranks. Findings are generated by the backend from thresholds
 * rather than hard-coded, so a newly exhausted quota, a newly throttled account
 * or a newly appeared resource produces a finding by itself with no code change
 * here (§8). "3 AI accounts appeared this week" is a finding on purpose:
 * management wants it, and it is how shadow provisioning surfaces.
 *
 * Two rendering rules:
 *
 * - **Severity is never colour-alone** (§3.2): every row carries the severity
 *   icon and the word, not just an accent stripe.
 * - **An empty list is stated, not blank.** "No finding" on a board whose whole
 *   purpose is finding things is itself information, and it has to be
 *   distinguishable from "the finding generator did not run" — which it is,
 *   because a payload that could not be built never reaches this component: the
 *   page renders the access panel instead.
 */

import { CircleCheck, Icon } from "@norican/icons";
import { Badge, Card, EmptyState, Typography } from "@norican/react";

import type { AiFinding } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { FINDING_COPY, findingCategoryLabel, formatCount, sortedFindings } from "./status";

interface AiFindingsPanelProps {
  findings: readonly AiFinding[];
  /** Anchor id, so a KPI tile or the sub-nav can land here. */
  id?: string;
}

export function AiFindingsPanel({ findings, id }: AiFindingsPanelProps) {
  const rows = sortedFindings(findings);
  const critical = rows.filter((finding) => finding.severity === "critical").length;
  const warnings = rows.filter((finding) => finding.severity === "warning").length;

  return (
    <section id={id} className="scroll-mt-6">
      <PanelCard
        title="What to look at"
        accentTone={critical > 0 ? "danger" : warnings > 0 ? "warning" : "success"}
        caption={
          rows.length === 0
            ? "No threshold was crossed"
            : `${formatCount(rows.length)} finding(s) · ${formatCount(critical)} critical, ${formatCount(warnings)} warning`
        }
      >
        {rows.length === 0 ? (
          <EmptyState
            size="sm"
            icon={<Icon icon={CircleCheck} size="lg" />}
            title="Nothing crossed a threshold in this snapshot"
            description="Every quota has headroom, no account is throttled above the warning rate, and nothing has appeared or disappeared. This is a measured result, not an empty panel — the figures above are what it was measured from."
          />
        ) : (
          <ul className="flex flex-col gap-3">
            {rows.map((finding) => {
              const copy = FINDING_COPY[finding.severity];
              return (
                <li key={`${finding.severity}-${finding.category}-${finding.headline}`}>
                  <Card variant="outline" accent="left" accentTone={copy.tone}>
                    <Card.Body padding="sm">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Severity carries an icon and a word, never colour alone. */}
                          <Icon icon={copy.icon} size="sm" tone={toneToIconTone(copy.tone)} />
                          <Badge tone={copy.tone} variant="subtle" size="xs">
                            {copy.label}
                          </Badge>
                          <Badge tone="neutral" variant="outline" size="xs">
                            {findingCategoryLabel(finding.category)}
                          </Badge>
                          <Typography variant="body-sm" as="span" className="font-medium">
                            {finding.headline}
                          </Typography>
                        </div>
                        <Typography variant="body-sm" tone="muted" as="p">
                          {finding.detail}
                        </Typography>
                        {finding.resource_id && (
                          <code className="text-fg-muted text-xs break-all">
                            {finding.resource_id}
                          </code>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </PanelCard>
    </section>
  );
}

/**
 * Map a status tone onto an `IconTone`.
 *
 * `IconTone` has no `neutral` member — a trap already paid for once — so the
 * neutral case returns `undefined` and the icon inherits `currentColor`, which is
 * exactly the muted treatment a neutral row wants.
 */
function toneToIconTone(tone: "success" | "warning" | "danger" | "neutral") {
  return tone === "neutral" ? undefined : tone;
}
